import { css } from 'astroturf';
import React from 'react';
import { CheatsheetCard } from "./cheatsheet-card";
import { Section, PropertyRow, CodeBlock } from "./cheatsheet-elements";

const styles = css`
  .twoColumnGrid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.25rem;
  }

  .commandPanel {
    background-color: var(--sheet-slate-900-50);
    border-radius: 0.25rem;
    padding: 0.375rem;
    border-width: 1px;
    border-style: solid;
    border-color: var(--sheet-slate-600-30);
  }

  .panelTitle {
    font-size: 10px;
    line-height: 1.2;
    font-weight: 600;
    color: var(--sheet-cyan-300);
    margin-bottom: 0.125rem;
    font-family: var(--font-inter);
  }

  .panelList {
    display: flex;
    flex-direction: column;
    row-gap: 0.125rem;
    font-size: 10px;
    line-height: 1.2;
  }

  .commandToken {
    color: var(--sheet-emerald-300);
  }

  .noteText {
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-400);
    display: flex;
    flex-direction: column;
    row-gap: 0.25rem;
    line-height: 1.25;
  }

  .noteLead {
    padding-bottom: 0.5rem;
  }

  .closedByList {
    font-size: 10px;
    line-height: 1.2;
    color: var(--sheet-slate-400);
    display: flex;
    flex-direction: column;
    row-gap: 0.125rem;
    margin-top: 0.5rem;
  }

  .customCommandNote {
    font-size: 11px;
    line-height: 1.25;
    color: var(--sheet-slate-400);
    margin-top: 0.25rem;
  }
`;

export function InvokerCheatsheet({ withBleed = false }) {
  return (
    <CheatsheetCard title="Invoker Commands" icon="invoker" withBleed={withBleed}>
      <div className={styles.twoColumnGrid}>
        <div>
          <Section title="HTML Attributes">
            <PropertyRow
              property="commandfor"
              values={["<element-id>"]}
              description="Target element to invoke (no #)"
            />
            <PropertyRow property="command" values={["<action>"]} description="Action to perform on target" />
          </Section>

          <Section title="Built-in Commands">
            <div className={styles.twoColumnGrid}>
              <div className={styles.commandPanel}>
                <div className={styles.panelTitle}>
                  Dialog
                </div>
                <div className={styles.panelList}>
                  <div>
                    <code className={styles.commandToken}>show-modal</code>
                  </div>
                  <div>
                    <code className={styles.commandToken}>close</code>
                  </div>
                  <div>
                    <code className={styles.commandToken}>request-close</code>
                  </div>
                </div>
              </div>

              <div className={styles.commandPanel}>
                <div className={styles.panelTitle}>
                  Popover
                </div>
                <div className={styles.panelList}>
                  <div>
                    <code className={styles.commandToken}>toggle-popover</code>
                  </div>
                  <div>
                    <code className={styles.commandToken}>show-popover</code>
                  </div>
                  <div>
                    <code className={styles.commandToken}>hide-popover</code>
                  </div>
                </div>
              </div>
            </div>
          </Section>

          <Section title="Dialog commands: close vs request-close">
            <div className={styles.noteText}>
              <div className={styles.noteLead}>
                <code className={styles.commandToken}>close</code> closes immediately
              </div>

              <div>
                <code className={styles.commandToken}>request-close</code> fires a `cancel` event on dialog.
                preventDefault() on this event to prevent close
              </div>
            </div>
          </Section>

          <Section title="Dialog closedby Attribute">
            <PropertyRow
              property="closedby"
              values={["any", "closerequest", "none"]}
              defaultValue="closerequest"
              description="How dialog is dismissed"
            />
            <div className={styles.closedByList}>
              <div>
                <code className={styles.commandToken}>any</code> light dismiss + Esc
              </div>
              <div>
                <code className={styles.commandToken}>closerequest</code> Esc only
              </div>
              <div>
                <code className={styles.commandToken}>none</code> explicit close only
              </div>
            </div>
          </Section>

          <Section title="Custom Commands">
            <PropertyRow property="command" values={["--custom-name"]} />
            <p className={styles.customCommandNote}>
              Dispatch a <code className={styles.commandToken}>CommandEvent</code> on the target.
            </p>
          </Section>

          <Section title="Tooltips">
            <PropertyRow
              property="interestfor"
              values={["<element-id>"]}
              description="Popover target to show on hover."
            />
          </Section>
        </div>

        <div>
          <Section title="Dialog Example">
            <CodeBlock language="markup">{`<button commandfor="dlg"
        command="show-modal">
  Open
</button>

<dialog id="dlg" closedby="any">
  <button commandfor="dlg"
   command="request-close">×</button>
</dialog>`}</CodeBlock>
            <CodeBlock language="javascript">{`dlg.addEventListener('cancel',
  (e) => {
    if (!canClose) e.preventDefault();
  });
`}</CodeBlock>
          </Section>

          <Section title="Popover Example">
            <CodeBlock language="markup">{`<button commandfor="menu"
        command="toggle-popover">
  Menu
</button>

<div id="menu" popover>
  Content
</div>`}</CodeBlock>
          </Section>

          <Section title="Custom Command">
            <CodeBlock language="markup">{`<button commandfor="vid"
    command="--play">Play</button>`}</CodeBlock>
            <CodeBlock language="javascript">{`vid.addEventListener('command',
  (e) => {
    if (e.command === '--play')
      vid.play();
  });`}</CodeBlock>
          </Section>
        </div>
      </div>
    </CheatsheetCard>
  );
}
